import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus, Inbox, Sparkles, Edit3, MapPin, Globe, BarChart3, Crown, Copy, ExternalLink, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ReferralPanel from "@/components/ReferralPanel";

function EnrichDialog({ open, onOpenChange, company, onSaved }) {
  const { lang } = useAuth();
  const [gallery, setGallery] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const uid = () => (globalThis.crypto?.randomUUID?.() ?? `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [team, setTeam] = useState([{ _uid: uid(), name: "", role: "" }]);
  const [news, setNews] = useState([{ _uid: uid(), title: "", body: "", date: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) {
      setGallery((company.gallery || []).join("\n"));
      setVideoUrl(company.video_url || "");
      setTeam(company.team?.length
        ? company.team.map((m) => ({ _uid: uid(), name: m.name || "", role: m.role || "" }))
        : [{ _uid: uid(), name: "", role: "" }]);
      setNews(company.news?.length
        ? company.news.map((n) => ({ _uid: uid(), title: n.title || "", body: n.body || "", date: n.date || "" }))
        : [{ _uid: uid(), title: "", body: "", date: "" }]);
    }
    // uid() is stable (never re-created), so lint-clean without adding it to deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gallery: gallery.split("\n").map((s) => s.trim()).filter(Boolean),
        video_url: videoUrl.trim() || null,
        team: team.filter((m) => m.name.trim()).map(({ _uid, ...rest }) => rest),
        news: news.filter((n) => n.title.trim()).map(({ _uid, ...rest }) => rest),
      };
      await http.put(`/companies/${company.id}/enrich`, payload);
      toast.success(t(lang, "enrich_saved"));
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="enrich-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t(lang, "enrich_title")}</DialogTitle>
          <p className="text-sm text-slate-500">{company?.name}</p>
        </DialogHeader>
        <div className="space-y-6">
          <div>
            <Label>{t(lang, "enrich_gallery_label")}</Label>
            <Textarea data-testid="enrich-gallery" value={gallery} onChange={(e) => setGallery(e.target.value)} rows={4} className="mt-1.5" placeholder="https://images.unsplash.com/…" />
          </div>

          <div>
            <Label>{lang === "fr" ? "Vidéo métier 30s (YouTube embed URL)" : "30s career video (YouTube embed URL)"}</Label>
            <Input
              data-testid="enrich-video"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/embed/XXXXXXX"
              className="mt-1.5"
            />
            <p className="text-xs text-slate-500 mt-1">
              {lang === "fr" ? "Collez l'URL embed d'une vidéo YouTube courte présentant votre métier ou entreprise." : "Paste the embed URL of a short YouTube video showcasing your job or company."}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t(lang, "team")}</Label>
              <Button size="sm" variant="outline" onClick={() => setTeam([...team, { _uid: uid(), name: "", role: "" }])} className="rounded-full text-xs h-7">+ {t(lang, "enrich_add")}</Button>
            </div>
            <div className="space-y-2">
              {team.map((m, i) => (
                <div key={m._uid} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <Input data-testid={`team-name-${i}`} placeholder={t(lang, "enrich_team_name")} value={m.name} onChange={(e) => setTeam(team.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <Input data-testid={`team-role-${i}`} placeholder={t(lang, "enrich_team_role")} value={m.role} onChange={(e) => setTeam(team.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} />
                  <Button size="sm" variant="ghost" onClick={() => setTeam(team.filter((_, j) => j !== i))} className="rounded-full">✕</Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t(lang, "news")}</Label>
              <Button size="sm" variant="outline" onClick={() => setNews([...news, { _uid: uid(), title: "", body: "", date: "" }])} className="rounded-full text-xs h-7">+ {t(lang, "enrich_add")}</Button>
            </div>
            <div className="space-y-3">
              {news.map((n, i) => (
                <div key={n._uid} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <Input data-testid={`news-title-${i}`} placeholder={t(lang, "enrich_news_title")} value={n.title} onChange={(e) => setNews(news.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
                    <Input data-testid={`news-date-${i}`} type="date" value={n.date} onChange={(e) => setNews(news.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} className="w-40" />
                  </div>
                  <Textarea data-testid={`news-body-${i}`} placeholder={t(lang, "enrich_news_body")} rows={2} value={n.body} onChange={(e) => setNews(news.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} />
                  <div className="flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setNews(news.filter((_, j) => j !== i))} className="text-xs">✕ {t(lang, "enrich_remove")}</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t(lang, "cancel")}</Button>
          <Button data-testid="enrich-save-btn" disabled={saving} onClick={save} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">
            {saving ? "..." : t(lang, "save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlacesImportDialog({ open, onOpenChange, onImported }) {
  const { lang } = useAuth();
  const [form, setForm] = useState({ lat: 48.8566, lng: 2.3522, radius_m: 5000 });
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/companies/import-places", form);
      toast.success(`${data.imported} ${t(lang, "import_success")}`);
      onImported && onImported();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t(lang, "import_no_key"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="places-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t(lang, "import_places")}</DialogTitle>
          <p className="text-sm text-slate-500">{t(lang, "import_places_sub")}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t(lang, "import_lat")}</Label><Input data-testid="places-lat" type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: parseFloat(e.target.value) })} /></div>
            <div><Label>{t(lang, "import_lng")}</Label><Input data-testid="places-lng" type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: parseFloat(e.target.value) })} /></div>
          </div>
          <div><Label>{t(lang, "import_radius")}</Label><Input data-testid="places-radius" type="number" min={500} max={50000} value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: parseInt(e.target.value) || 5000 })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t(lang, "cancel")}</Button>
          <Button data-testid="places-run-btn" disabled={busy} onClick={run} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full">
            <MapPin className="w-4 h-4 mr-1.5" /> {busy ? "..." : t(lang, "import_run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompanyDashboard() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [mine, setMine] = useState([]);
  const [apps, setApps] = useState([]);
  const [vivier, setVivier] = useState([]);
  const [enrichCo, setEnrichCo] = useState(null);
  const [placesOpen, setPlacesOpen] = useState(false);

  const load = () => {
    http.get("/companies/mine/list").then((r) => setMine(r.data));
    http.get("/applications/received").then((r) => setApps(r.data));
    http.get("/hr/vivier").then((r) => setVivier(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
  }, [user, nav, loading]);

  const addVivier = async (appId) => {
    try {
      await http.post(`/hr/vivier/${appId}`);
      toast.success(t(lang, "add_to_vivier"));
      load();
    } catch (e) {
      // Surface transient failures so users don't wonder why nothing happened,
      // and keep a dev log for local debugging.
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Ajout au vivier impossible" : "Add to talent pool failed"));
    }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(lang === "fr"
      ? `Supprimer définitivement « ${c.name} » et son site web IA ? Cette action est irréversible.`
      : `Permanently delete « ${c.name} » and its AI website? This is irreversible.`)) return;
    try {
      await http.delete(`/companies/${c.id}`);
      toast.success(lang === "fr" ? "Entreprise supprimée" : "Company deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteSite = async (c) => {
    if (!window.confirm(lang === "fr"
      ? `Dépublier et supprimer le site IA de « ${c.name} » ?`
      : `Unpublish and delete AI site for « ${c.name} »?`)) return;
    try {
      await http.delete(`/websites/${c.id}`);
      toast.success(lang === "fr" ? "Site supprimé" : "Site deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const isAdmin = user?.user_type === "admin";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "company_dash_title")}</h1>
            <p className="text-slate-500 text-sm mt-1">{lang === "fr" ? "Gérez vos fiches vitrine et vos candidatures reçues." : "Manage your pages and incoming applications."}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <Button data-testid="import-places-btn" onClick={() => setPlacesOpen(true)} variant="outline" className="rounded-full h-11 border-[#1E3A8A] text-[#1E3A8A]">
                <MapPin className="w-4 h-4 mr-1.5" />{t(lang, "import_places")}
              </Button>
            )}
            <Link to="/company/new">
              <Button data-testid="create-company-btn" className="bg-[#F97316] hover:bg-[#EA580C] rounded-full h-11"><Plus className="w-4 h-4 mr-1.5" />{t(lang, "company_new")}</Button>
            </Link>
          </div>
        </div>

        <section className="mt-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{t(lang, "my_companies")}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.map((c) => {
              const publicUrl = `${window.location.origin}/vitrine/${c.id}`;
              return (
              <div key={c.id} data-testid={`my-co-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.city}, {c.country}</p>
                  </div>
                  <Badge className={c.verified ? "bg-green-100 text-green-800 rounded-full" : "bg-amber-100 text-amber-800 rounded-full"}>{c.verified ? "✓" : "⏳"}</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description}</p>
                {/* Publicly-shareable web page URL — this IS the company's website. */}
                <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs">
                  <Globe className="w-3 h-3 text-[#1E3A8A] shrink-0" />
                  <span className="truncate text-slate-700 flex-1 font-mono" title={publicUrl}>{publicUrl}</span>
                  <button
                    data-testid={`copy-url-${c.id}`}
                    onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success(t(lang, "copied")); }}
                    className="text-slate-500 hover:text-[#1E3A8A]"
                    aria-label="Copy URL"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid={`enrich-${c.id}`} size="sm" onClick={() => setEnrichCo(c)} className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full text-xs h-8">
                    <Edit3 className="w-3 h-3 mr-1" /> {t(lang, "enrich_page")}
                  </Button>
                  <Link to={`/vitrine/${c.id}`} target="_blank">
                    <Button size="sm" variant="outline" className="rounded-full text-xs h-8">
                      <ExternalLink className="w-3 h-3 mr-1" /> {t(lang, "vitrine_public")}
                    </Button>
                  </Link>
                  {/* AI Website Studio — full standalone 1-page website generator */}
                  <Link to={`/company/${c.id}/website`}>
                    <Button data-testid={`studio-${c.id}`} size="sm" className="bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:opacity-90 rounded-full text-xs h-8">
                      <Sparkles className="w-3 h-3 mr-1" /> {lang === "fr" ? "Site web IA" : "AI Website"}
                    </Button>
                  </Link>
                  <Button data-testid={`del-site-${c.id}`} size="sm" variant="ghost" onClick={() => deleteSite(c)} className="rounded-full text-xs h-8 text-slate-400 hover:text-red-600" title={lang === "fr" ? "Supprimer le site" : "Delete site"}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  <Button data-testid={`del-co-${c.id}`} size="sm" variant="ghost" onClick={() => deleteCompany(c)} className="rounded-full text-xs h-8 text-slate-400 hover:text-red-600 ml-auto" title={lang === "fr" ? "Supprimer l'entreprise" : "Delete company"}>
                    <Trash2 className="w-3 h-3 mr-1" /> {lang === "fr" ? "Supprimer" : "Delete"}
                  </Button>
                </div>
              </div>
            );})}
            {mine.length === 0 && (
              <div className="col-span-full text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl">
                <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">—</p>
              </div>
            )}
          </div>
        </section>

        {/* Monetisation & data toolkit — visible on every dashboard, not
            hidden behind a menu. Each card links to an existing feature. */}
        <section className="mt-10 grid md:grid-cols-3 gap-4">
          <Link
            to="/pricing"
            data-testid="dash-cta-pricing"
            className="group rounded-2xl bg-gradient-to-br from-[#F97316] to-[#EA580C] text-white p-5 hover:shadow-lg transition-shadow"
          >
            <Crown className="w-6 h-6 mb-3" />
            <p className="font-display font-semibold">{t(lang, "dash_pricing_title")}</p>
            <p className="text-sm text-white/85 mt-1 leading-snug">{t(lang, "dash_pricing_sub")}</p>
            <p className="text-xs text-white/80 mt-3 group-hover:underline">{t(lang, "dash_pricing_cta")} →</p>
          </Link>

          <Link
            to="/analytics"
            data-testid="dash-cta-analytics"
            className="group rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white p-5 hover:shadow-lg transition-shadow"
          >
            <BarChart3 className="w-6 h-6 mb-3" />
            <p className="font-display font-semibold">{t(lang, "dash_analytics_title")}</p>
            <p className="text-sm text-white/85 mt-1 leading-snug">{t(lang, "dash_analytics_sub")}</p>
            <p className="text-xs text-white/80 mt-3 group-hover:underline">{t(lang, "dash_analytics_cta")} →</p>
          </Link>

          <Link
            to={mine[0] ? `/company/${mine[0].id}/website` : "/company/new"}
            data-testid="dash-cta-website-studio"
            className="group rounded-2xl bg-white border-2 border-dashed border-[#F97316]/40 p-5 hover:border-[#F97316] transition-colors relative overflow-hidden"
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#F97316]/10 blur-2xl" />
            <Sparkles className="w-6 h-6 mb-3 text-[#F97316]" />
            <p className="font-display font-semibold text-slate-900">
              {lang === "fr" ? "Studio Site Web IA" : "AI Website Studio"}
            </p>
            <p className="text-sm text-slate-600 mt-1 leading-snug">
              {lang === "fr" ? "Générez un vrai site web 1-page hébergé sur myproxijob.com/site/…" : "Generate a real 1-page website hosted on myproxijob.com/site/…"}
            </p>
            <p className="text-xs text-[#F97316] mt-3 group-hover:underline">
              {lang === "fr" ? "Créer avec l'IA" : "Create with AI"} →
            </p>
          </Link>
        </section>

        <ReferralPanel />

        <section className="mt-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{t(lang, "applications_received")}</p>
          <div className="space-y-3">
            {apps.map((a) => (
              <div key={a.id} data-testid={`rec-app-${a.id}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{a.candidate_name}</p>
                    <p className="text-xs text-slate-500">{a.candidate_email} · {new Date(a.created_at).toLocaleDateString()}</p>
                    {a.position_wanted && <p className="text-sm text-[#1E3A8A] mt-1">→ {a.position_wanted}</p>}
                  </div>
                  <Badge variant="secondary" className="rounded-full">{t(lang, `status_${a.status}`)}</Badge>
                </div>
                <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3">{a.message}</p>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => addVivier(a.id)} className="rounded-full text-xs" data-testid={`vivier-add-${a.id}`}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> {t(lang, "add_to_vivier")}
                  </Button>
                </div>
              </div>
            ))}
            {apps.length === 0 && (
              <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl">
                <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">—</p>
              </div>
            )}
          </div>
        </section>

        <section className="mt-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t(lang, "vivier_title")}</p>
          <p className="text-xs text-slate-500 mb-4">{t(lang, "vivier_sub")}</p>
          <div className="grid md:grid-cols-2 gap-3">
            {vivier.map((v) => (
              <div key={`${v.company_id}_${v.candidate_id}`} data-testid={`vivier-${v.candidate_id}`} className="rounded-2xl bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/30 p-4">
                <div className="flex items-center gap-2 mb-1"><Sparkles className="w-3.5 h-3.5 text-[#C2410C]" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#C2410C] font-semibold">Vivier</span></div>
                <p className="font-semibold text-sm">{v.candidate_name}</p>
                <p className="text-xs text-slate-500">{v.candidate_email}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(v.added_at).toLocaleDateString()}</p>
              </div>
            ))}
            {vivier.length === 0 && <p className="text-sm text-slate-500 col-span-full text-center py-6">—</p>}
          </div>
        </section>
      </div>

      <EnrichDialog open={!!enrichCo} onOpenChange={(v) => !v && setEnrichCo(null)} company={enrichCo} onSaved={load} />
      <PlacesImportDialog open={placesOpen} onOpenChange={setPlacesOpen} onImported={load} />
    </Layout>
  );
}
